import React, { useState, useEffect } from 'react';

// De API-call is hier verwijderd. De Heatmap ontvangt nu direct de "serverFatigueData" uit Stats.js
export default function MuscleHeatmap({ user, serverFatigueData }) {
    const [activeMuscle, setActiveMuscle] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Een kleine "fake" delay zodat het skelet niet flikkert tijdens het renderen
        const timer = setTimeout(() => {
            setLoading(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [serverFatigueData]);

    if (loading) {
        return <div className="text-zinc-500 text-xs font-bold uppercase tracking-widest text-center p-8 animate-pulse">Laden van spier status profiel...</div>;
    }

    if (!serverFatigueData || Object.keys(serverFatigueData).length === 0) {
         return <div className="text-zinc-500 text-xs font-bold uppercase tracking-widest text-center p-8">Geen recente data beschikbaar. Log een training.</div>;
    }

    const p = serverFatigueData;

    const getColor = (pct) => {
        if (!pct || pct <= 0) return '#4ade80';
        if (pct >= 80) return '#b91c1c';
        if (pct >= 60) return '#ef4444';
        if (pct >= 40) return '#f97316';
        if (pct >= 20) return '#eab308';
        return '#4ade80';
    };

    const pathStyle = "transition-all duration-700 ease-in-out stroke-zinc-900 stroke-[1.5] cursor-pointer hover:stroke-white";

    const getShadow = (percentage, color) => {
        if (percentage >= 60) return `drop-shadow(0px 0px 6px ${color})`;
        return 'none';
    };

    const handleTap = (muscleName, percentage) => {
        setActiveMuscle({ name: muscleName, value: percentage || 0 });
        setTimeout(() => setActiveMuscle(null), 3000);
    };

    return (
        <div className="bg-[#121214] border border-zinc-800 rounded-3xl p-5 sm:p-7 shadow-2xl w-full mb-8 relative">
            <div className="mb-8">
                <div className="flex justify-between items-end mb-4">
                    <div>
                        <h3 className="text-orange-500 font-black uppercase tracking-widest text-sm">Spier Status</h3>
                        <p className="text-gray-400 text-xs mt-1 font-medium">Dynamisch server herstelprofiel</p>
                    </div>
                    <div className="text-right">
                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Systeem (CNS)</span>
                        <div className="text-xl font-black text-white">{p.cns || 0}%</div>
                    </div>
                </div>

                <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                        className="h-full transition-all duration-1000 ease-out rounded-full"
                        style={{
                            width: `${Math.min(100, Math.max(0, p.cns || 0))}%`,
                            backgroundColor: getColor(p.cns),
                            boxShadow: p.cns >= 60 ? `0 0 10px ${getColor(p.cns)}` : 'none'
                        }}
                    ></div>
                </div>
            </div>

            {activeMuscle && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 bg-black/90 border border-zinc-700 p-4 rounded-xl shadow-2xl backdrop-blur-sm animate-fade-in text-center w-40">
                    <span className="block text-gray-400 text-[10px] uppercase font-bold tracking-wider">{activeMuscle.name}</span>
                    <span className="block text-3xl font-black text-white">{activeMuscle.value}%</span>
                    <span className="block text-[10px] text-gray-500 mt-1 uppercase">Vermoeid</span>
                </div>
            )}

            <div className="flex justify-around items-center h-64 sm:h-80 overflow-visible relative">
                <div className="relative h-full flex flex-col items-center w-1/2">
                    <span className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-widest mb-4">Voorzijde</span>
                    <svg viewBox="0 0 100 200" className="h-full overflow-visible">
                        <circle cx="50" cy="15" r="10" fill="#27272a" className="stroke-zinc-900 stroke-2" />
                        <path d="M 35 30 Q 50 40 65 30 Q 68 45 50 50 Q 32 45 35 30 Z" fill={getColor(p.chest)} className={pathStyle} style={{ filter: getShadow(p.chest, getColor(p.chest)) }} onClick={() => handleTap('Borst', p.chest)} />
                        <path d="M 38 52 Q 50 55 62 52 L 58 75 Q 50 78 42 75 Z" fill={getColor(p.core)} className={pathStyle} style={{ filter: getShadow(p.core, getColor(p.core)) }} onClick={() => handleTap('Core', p.core)} />
                        <path d="M 32 30 Q 20 28 17 42 L 25 48 Z" fill={getColor(p.shoulders)} className={pathStyle} onClick={() => handleTap('Schouders', p.shoulders)} />
                        <path d="M 68 30 Q 80 28 83 42 L 75 48 Z" fill={getColor(p.shoulders)} className={pathStyle} onClick={() => handleTap('Schouders', p.shoulders)} />
                        <path d="M 17 44 Q 10 58 14 68 L 23 64 Z" fill={getColor(p.biceps)} className={pathStyle} onClick={() => handleTap('Biceps', p.biceps)} />
                        <path d="M 83 44 Q 90 58 86 68 L 77 64 Z" fill={getColor(p.biceps)} className={pathStyle} onClick={() => handleTap('Biceps', p.biceps)} />
                        <path d="M 40 82 Q 26 110 32 135 L 43 135 L 47 82 Z" fill={getColor(p.quads)} className={pathStyle} style={{ filter: getShadow(p.quads, getColor(p.quads)) }} onClick={() => handleTap('Quads', p.quads)} />
                        <path d="M 60 82 Q 74 110 68 135 L 57 135 L 53 82 Z" fill={getColor(p.quads)} className={pathStyle} style={{ filter: getShadow(p.quads, getColor(p.quads)) }} onClick={() => handleTap('Quads', p.quads)} />
                        <path d="M 33 140 Q 26 160 30 175 L 40 175 Z" fill={getColor(p.calves)} className={pathStyle} onClick={() => handleTap('Kuiten (Voor)', p.calves)} />
                        <path d="M 67 140 Q 74 160 70 175 L 60 175 Z" fill={getColor(p.calves)} className={pathStyle} onClick={() => handleTap('Kuiten (Voor)', p.calves)} />
                    </svg>
                </div>

                <div className="relative h-full flex flex-col items-center w-1/2">
                    <span className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-widest mb-4">Achterzijde</span>
                    <svg viewBox="0 0 100 200" className="h-full overflow-visible">
                        <circle cx="50" cy="15" r="10" fill="#27272a" className="stroke-zinc-900 stroke-2" />
                        <path d="M 35 28 Q 50 35 65 28 L 75 42 L 25 42 Z" fill={getColor(p.shoulders)} className={pathStyle} onClick={() => handleTap('Schouders/Traps', p.shoulders)} />
                        <path d="M 28 44 Q 50 70 50 75 Q 35 65 38 44 Z" fill={getColor(p.back)} className={pathStyle} onClick={() => handleTap('Rug (Lats)', p.back)} />
                        <path d="M 72 44 Q 50 70 50 75 Q 65 65 62 44 Z" fill={getColor(p.back)} className={pathStyle} onClick={() => handleTap('Rug (Lats)', p.back)} />
                        <path d="M 38 72 Q 50 68 62 72 L 58 82 Q 50 85 42 82 Z" fill={getColor(p.lower_back)} className={pathStyle} style={{ filter: getShadow(p.lower_back, getColor(p.lower_back)) }} onClick={() => handleTap('Onderrug', p.lower_back)} />
                        <path d="M 23 44 Q 10 58 14 68 L 20 64 Z" fill={getColor(p.triceps)} className={pathStyle} onClick={() => handleTap('Triceps', p.triceps)} />
                        <path d="M 77 44 Q 90 58 86 68 L 80 64 Z" fill={getColor(p.triceps)} className={pathStyle} onClick={() => handleTap('Triceps', p.triceps)} />
                        <path d="M 38 85 Q 50 90 62 85 Q 68 102 50 105 Q 32 102 38 85 Z" fill={getColor(p.glutes)} className={pathStyle} style={{ filter: getShadow(p.glutes, getColor(p.glutes)) }} onClick={() => handleTap('Bilspieren', p.glutes)} />
                        <path d="M 36 106 Q 28 120 32 135 L 43 135 L 47 106 Z" fill={getColor(p.hamstrings)} className={pathStyle} style={{ filter: getShadow(p.hamstrings, getColor(p.hamstrings)) }} onClick={() => handleTap('Hamstrings', p.hamstrings)} />
                        <path d="M 64 106 Q 72 120 68 135 L 57 135 L 53 106 Z" fill={getColor(p.hamstrings) } className={pathStyle} style={{ filter: getShadow(p.hamstrings, getColor(p.hamstrings)) }} onClick={() => handleTap('Hamstrings', p.hamstrings)} />
                        <path d="M 33 140 Q 22 155 30 175 L 40 175 L 43 140 Z" fill={getColor(p.calves)} className={pathStyle} onClick={() => handleTap('Kuiten (Achter)', p.calves)} />
                        <path d="M 67 140 Q 78 155 70 175 L 60 175 L 57 140 Z" fill={getColor(p.calves)} className={pathStyle} onClick={() => handleTap('Kuiten (Achter)', p.calves)} />
                    </svg>
                </div>
            </div>

            <div className="flex justify-center gap-3 sm:gap-6 mt-6 text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-widest flex-wrap">
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#4ade80]"></span> Fris</div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#eab308]"></span> Licht</div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#f97316]"></span> Belast</div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#ef4444]"></span> Zwaar</div>
                <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[#b91c1c] shadow-[0_0_8px_#b91c1c]"></span> Maximaal</div>
            </div>
        </div>
    );
}
